import { useMemo } from 'react';
import { t } from '@lingui/macro';

import { useId } from '../../page-framework/panel/Panel';
import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import PanelActions from '../../page-framework/panel/PanelActions';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import ChartTypeForm from '../../charts/ipm/ChartTypeForm';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { interfaceFilterId, timeRangeFilterId } from '../../filters';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';

const title = t`Interface Discards`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'interface',
        metrics: [
            'statusInterfaceDiscards',
            'discardsIn',
            'discardsOut',
            'discardsInCount',
            'discardsOutCount',
            'discardsInPercent',
            'discardsOutPercent',
        ],
    },
};

const RATE = 'RATE';
const COUNT = 'COUNT';
const PERCENT = 'PERCENT';

const typeConfig = {
    [RATE]: {
        formatter: FORMATTER_TYPES.LOG,
        yAxisTitle: t`Discards/min`,
        units: ` ${t`Discards/min`}`,
        inMetric: 'discardsIn',
        outMetric: 'discardsOut',
    },
    [COUNT]: {
        formatter: FORMATTER_TYPES.LOG,
        yAxisTitle: t`Discards`,
        units: ` ${t`Discards`}`,
        inMetric: 'discardsInCount',
        outMetric: 'discardsOutCount',
    },
    [PERCENT]: {
        formatter: FORMATTER_TYPES.PERCENT,
        forceDecimals: true,
        yAxisTitle: t`Discards (%)`,
        units: '%',
        inMetric: 'discardsInPercent',
        outMetric: 'discardsOutPercent',
    },
};

function useChartConfig(type) {
    const inColor = useMetricColor('networkIn');
    const outColor = useMetricColor('networkOut');
    const config = typeConfig[type];

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: config.yAxisTitle,
                    },
                },
            ],
            tooltip: BuiltInTooltip,
            showGranularity: true,
            statusMetric: 'statusInterfaceDiscards',
            chartTrends: [
                {
                    formatter: config.formatter,
                    units: config.units,
                    chartType: 'spline',
                    key: config.inMetric,
                    label: t`In`,
                    color: inColor,
                    multiplier: 1,
                    forceDecimals: config.forceDecimals === true,
                },
                {
                    formatter: config.formatter,
                    units: config.units,
                    chartType: 'spline',
                    key: config.outMetric,
                    label: t`Out`,
                    color: outColor,
                    multiplier: 1,
                    forceDecimals: config.forceDecimals === true,
                },
            ],
        }),
        [config, inColor, outColor]
    );
}

const selections = [
    { value: RATE, label: t`Rate` },
    { value: COUNT, label: t`Count` },
    { value: PERCENT, label: '%' },
];

function InterfaceDiscardsPanel() {
    const id = useId();
    const pageStateKey = `${id}-ChartType`;
    const [chartDisplayType, setChartDisplayType] = useChartDisplayType(pageStateKey, PERCENT);
    const data = usePanelDataService(dataSource);
    const chartConfig = useChartConfig(chartDisplayType);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return (
        <>
            <PanelActions>
                <ChartTypeForm value={chartDisplayType} onChangeType={setChartDisplayType} selections={selections} />
            </PanelActions>
            <PerformanceChart chartDef={chartConfig} data={chartData} />
        </>
    );
}

InterfaceDiscardsPanel.propTypes = panelPropTypes;

InterfaceDiscardsPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'InterfaceDiscards',
    catalogEntry: {
        category: PANEL_CATEGORIES.INTERFACE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Discards trend for a selected interface`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, interfaceFilterId],
        optional: [],
    },
})(InterfaceDiscardsPanel);
